// When may a registry preview be CONFIRMED? ONE definition, read by the server
// (which refuses) and the page (which explains before the click). PURE.
//
// ⚠️ The server is the authority — the page only decides whether to offer the
// button and what to say instead. Two copies of this rule would be two answers.
//
// ⚠️ The confirm is only as good as the preview it rests on, so a preview a
// human could not fully read cannot be confirmed: one that failed, one whose
// group names were not all returned, one that hit the page cap (its club list
// may be truncated), or one superseded by a newer preview.
//
// ⚠️ NO TIME LIMIT, deliberately — see confirmCopy.ts. The group ids are pinned
// by --expect-groups; the club set is re-read at commit time and the dialog
// says so; a newer preview supersedes an older one.

import type { CrmOnboardingRunKind, CrmOnboardingRunStatus } from '../../types/crmOnboarding.js';
import type { RegistryResult } from './registryResult.js';

export interface PreviewLike {
  id: string;
  kind: CrmOnboardingRunKind;
  brandKey: string;
  status: CrmOnboardingRunStatus;
  result: RegistryResult | null;
}

/** Why this preview cannot be confirmed, or null if it can. */
export function commitRefusal(
  preview: PreviewLike | null,
  brandKey: string,
  latestPreviewId: string | null,
): string | null {
  if (!preview || preview.kind !== 'registry-preview') return 'That is not a registry preview.';
  if (preview.brandKey !== brandKey) return 'That preview is for a different brand.';
  if (preview.status === 'running') return 'That preview is still running. Wait for it to finish and read it.';
  if (preview.status !== 'succeeded') return 'That preview did not finish cleanly. Run a fresh preview.';
  if (latestPreviewId !== null && latestPreviewId !== preview.id) {
    return 'A newer preview exists. Read and confirm that one instead.';
  }
  const r = preview.result;
  if (!r || r.dryRun !== true) return 'That preview has no readable result to confirm.';
  if (r.groupNames.length === 0) return 'That preview showed no groups.';
  if (r.groupNames.some((g) => g.name === null)) {
    return 'That preview could not show every group’s name, so it cannot be confirmed as ours.';
  }
  if (r.reachedPageCap) return 'That preview hit its page cap, so its club list may be incomplete.';
  return null;
}
